import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useModuleOn } from '@/lib/modules';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { CornerDownLeft, Search } from 'lucide-react';
import type MiniSearch from 'minisearch';
import { useDebounced } from '@/lib/useDebounced';
import { loadHelpIndex, searchCustom, searchHelp, type HelpHit } from '@/help/search';
import type { HelpSearchDoc } from '@/help/searchOptions';
import { areaTitle, helpHref, useCustomArticles, useVisibleGuides } from './data';

const esc = (t: string) => t.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
/** KB snippets mark matches with «»; turn them into <mark> like the guide search. */
const kbMarks = (t: string) => esc(t).replace(/«/g, '<mark>').replace(/»/g, '</mark>');

/** Company knowledge-base articles for a query (#61). The API applies the KB's own visibility rules. */
function useKbHits(q: string, enabled: boolean): { hits: HelpHit[]; loading: boolean } {
  const ws = useWorkspaceId();
  const t = q.trim();
  const on = enabled && Boolean(ws) && t.length >= 2;
  const r = useQuery({ queryKey: ['kb-search', ws, t], queryFn: () => api.kb.articles(ws!, { q: t, limit: 10 }), enabled: on, staleTime: 30_000 });
  const hits = useMemo(() => (on ? (r.data ?? []).filter(a => a.status === 'published' && !a.is_template) : []).map((a, i): HelpHit => ({
    key: `kb/${a.id}`, href: `/kb/${a.id}`, anchor: null, heading: a.collection_name, title: a.title, area: 'kb', source: 'company',
    titleHtml: esc(a.title), snippet: a.snippet ? kbMarks(a.snippet) : esc(a.summary ?? ''), // An article whose title contains the query outranks a guide that only mentions it in passing.
    score: (a.title.toLowerCase().includes(t.toLowerCase()) ? 1.05 : 0.9) - i * 0.05,
  })), [on, r.data, t]);
  return { hits, loading: on && r.isLoading };
}

/** t612u: published policies you can read, from Policies (when the module is on), so Help & knowledge base finds them too. */
function usePolicyHits(q: string, enabled: boolean): { hits: HelpHit[]; loading: boolean } {
  const ws = useWorkspaceId();
  const t = q.trim();
  const moduleOn = useModuleOn('policies');
  const on = enabled && moduleOn && Boolean(ws) && t.length >= 2;
  const r = useQuery({ queryKey: ['policies', ws ?? '', 'help-search', t], queryFn: () => api.search(ws!, t, { type: 'policies', limit: 5 }), enabled: on, staleTime: 30_000 });
  const hits = useMemo(() => (on ? (r.data?.policies ?? []) : []).map((p, i): HelpHit => ({
    key: `policies/${p.id}`, href: `/policies/${p.id}`, anchor: null, heading: p.type_name, title: p.title, area: 'policies', source: 'company',
    titleHtml: esc(p.title), snippet: p.snippet ? kbMarks(p.snippet) : '',
    score: (p.title.toLowerCase().includes(t.toLowerCase()) ? 1.05 : 0.9) - i * 0.05,
  })), [on, r.data, t]);
  return { hits, loading: on && r.isLoading };
}

/**
 * Guide + workspace article search, permission-filtered. The index chunk loads on the first query.
 * `kb` (#61, the combined Help & knowledge base) also searches company knowledge-base articles and (t612u) published
 * policies; results carry a source.
 */
export function useHelpSearch(q: string, limit = 20, opts: { kb?: boolean } = {}): { hits: HelpHit[]; loading: boolean } {
  const [ms, setMs] = useState<MiniSearch<HelpSearchDoc> | null>(null);
  const want = Boolean(q.trim());
  useEffect(() => { if (want && !ms) void loadHelpIndex().then(setMs); }, [want, ms]);
  const guides = useVisibleGuides().data;
  const custom = useCustomArticles().data;
  const kbArticles = useKbHits(q, Boolean(opts.kb));
  const policies = usePolicyHits(q, Boolean(opts.kb));
  const kb = useMemo(() => ({ hits: [...kbArticles.hits, ...policies.hits], loading: kbArticles.loading || policies.loading }), [kbArticles, policies]);
  const hits = useMemo(() => {
    if (!q.trim()) return [];
    const allowed = new Set(guides.map(g => g.key));
    const g = ms ? searchHelp(ms, q, k => allowed.has(k), limit) : [];
    const c = searchCustom((custom ?? []).filter(a => a.status === 'published'), q, 8);
    const local = [...g, ...c].sort((a, b) => b.score - a.score);
    if (!kb.hits.length) return local.slice(0, limit);
    // Guide scores are unbounded; scale them to 0..1 so the two kinds interleave by rank.
    const top = local[0]?.score || 1;
    return [...local.map(h => ({ h, n: h.score / top })), ...kb.hits.map(h => ({ h, n: h.score }))].sort((a, b) => b.n - a.n).map(x => x.h).slice(0, limit);
  }, [ms, q, guides, custom, limit, kb.hits]);
  return { hits, loading: (want && !ms) || kb.loading };
}

/** "Help" for the built-in guide, "Company" for anything your workspace wrote (its own guides, knowledge base). */
export function SourceBadge({ h }: { h: HelpHit }) {
  const company = h.source === 'company';
  return <span className={clsx('shrink-0 text-[10px] font-semibold uppercase tracking-wide', company ? 'text-amber-900 dark:text-amber-100' : 'text-slate-700 dark:text-slate-200')} data-testid="help-hit-source">{company ? 'Company' : 'Help'}</span>;
}

export const hitHref = (h: HelpHit) => h.href ?? helpHref(h.key, h.anchor);

export function HitRow({ h, active, showSource }: { h: HelpHit; active?: boolean; showSource?: boolean }) {
  return (
    <span className={clsx('help-hit block min-w-0 flex-1', active && 'text-slate-900 dark:text-white')}>
      <span className="flex items-center gap-2"><span className="block min-w-0 truncate font-medium" dangerouslySetInnerHTML={{ __html: h.titleHtml }} />{showSource && <SourceBadge h={h} />}</span>
      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{h.area === 'kb' ? 'Knowledge base' : h.key.startsWith('policies/') ? 'Policies' : areaTitle(h.area)}{h.heading ? ` › ${h.heading}` : ''}</span>
      {h.snippet && <span className="mt-0.5 line-clamp-2 block text-xs text-slate-600 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: h.snippet }} />}
    </span>
  );
}

/**
 * The Help Centre search box: results as you type, ↑/↓ to move, Enter to open, Esc to clear.
 * `onPick` overrides navigation (the side panel opens articles in place).
 */
export function HelpSearchBox({ autoFocus, onPick, initial = '', size = 'lg', kb = false, placeholder = 'Search the guide: “book holiday”, “swap a shift”, “invite someone”…' }: {
  autoFocus?: boolean; onPick?: (h: HelpHit) => void; initial?: string; size?: 'lg' | 'md'; placeholder?: string;
  /** Also search company knowledge-base articles (the combined Help & knowledge base page). */
  kb?: boolean;
}) {
  const nav = useNavigate();
  const [q, setQ] = useState(initial);
  const dq = useDebounced(q, 120);
  const { hits, loading } = useHelpSearch(dq, 8, { kb });
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => setI(0), [dq]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const pick = (h: HelpHit) => { setOpen(false); if (onPick) onPick(h); else nav(hitHref(h)); };
  const all = () => { setOpen(false); nav(`/help/search?q=${encodeURIComponent(q.trim())}`); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setI(x => Math.min(x + 1, hits.length)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setI(x => Math.max(x - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hits[i]) pick(hits[i]); else if (q.trim() && !onPick) all(); }
    else if (e.key === 'Escape') { if (q) { e.preventDefault(); setQ(''); } setOpen(false); }
  };
  const show = open && Boolean(dq.trim());
  const activeId = show && hits[i] ? `${id}-${i}` : undefined;
  return (
    <div ref={box} className="help-search relative" data-testid="help-search">
      <label className={clsx('flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 focus-field dark:border-slate-700 dark:bg-slate-900', size === 'lg' ? 'h-12 text-base shadow-sm' : 'h-10 text-sm')}>
        <Search className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
        <span className="sr-only">Search the Help Centre</span>
        <input value={q} autoFocus={autoFocus} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => { if (q !== initial) setOpen(true); }} onKeyDown={onKey}
          placeholder={placeholder} className="w-full bg-transparent outline-none placeholder:text-slate-500"
          role="combobox" aria-expanded={show} aria-controls={`${id}-list`} aria-activedescendant={activeId} aria-autocomplete="list" data-testid="help-search-input" />
      </label>
      {show && (
        <ul id={`${id}-list`} role="listbox" aria-label="Help results" className="absolute inset-x-0 top-full z-30 mt-1 max-h-[60vh] overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900" data-testid="help-search-results">
          {hits.map((h, idx) => (
            <li key={`${h.key}#${h.anchor ?? ''}`} id={`${id}-${idx}`} role="option" aria-selected={idx === i} onMouseMove={() => setI(idx)} onMouseDown={e => { e.preventDefault(); pick(h); }}
              className={clsx('flex cursor-pointer items-start gap-3 px-3 py-2 text-sm', idx === i && 'bg-slate-100 dark:bg-slate-800')}>
              <HitRow h={h} active={idx === i} showSource={kb} />
              {idx === i && <CornerDownLeft className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />}
            </li>
          ))}
          {!hits.length && <li className="px-3 py-4 text-center text-sm text-slate-500 dark:text-slate-400">{loading ? 'Searching…' : 'Nothing found. Try fewer or different words.'}</li>}
          {!onPick && hits.length > 0 && (
            <li role="option" aria-selected={i === hits.length} id={`${id}-${hits.length}`} onMouseMove={() => setI(hits.length)} onMouseDown={e => { e.preventDefault(); all(); }}
              className={clsx('cursor-pointer border-t border-slate-100 px-3 py-2 text-sm dark:border-slate-800', i === hits.length && 'bg-slate-100 dark:bg-slate-800')}>
              See all results for “{dq.trim()}”
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
